import { Flex } from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import { ApiKeysManagement } from 'components/sections/admin-portal/api-keys';
import { BallTypesTable } from 'components/sections/admin-portal/ball-types/table';
import { ErrorTypesTableHoc } from 'components/sections/admin-portal/error-types/table';
import { MachineModelsTable } from 'components/sections/admin-portal/machine-models/table';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { MachinesTable } from 'components/sections/admin-portal/machines/table';
import { ServerControlsTab } from 'components/sections/admin-portal/server-controls';
import { ServerErrorsTableHoC } from 'components/sections/admin-portal/server-errors/table';
import { AdminTabNav } from 'components/sections/admin-portal/tab-nav';
import { TeamsTable } from 'components/sections/admin-portal/teams/table';
import { UsersTable } from 'components/sections/admin-portal/users/table';
import { VideoLibrary } from 'components/sections/video-library';
import { TeamsProvider } from 'contexts/admin/teams.context';
import { UsersProvider } from 'contexts/admin/users.context';
import { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { paths, routePaths } from 'routes/paths';
import { useAuthStore } from 'stores/use-auth-store';

const RedirectNav = () => <Navigate to={paths.admin.default} replace />;

export const AdminPortal = () => {
  const userRoleFeatureFlags = useAuthStore((s) => s.userRoleFeatureFlags);
  const current = useAuthStore((s) => s.current);
  const { sections } = userRoleFeatureFlags;

  const fetchMachinesAndModels = useMachinesPortalStore(
    (s) => s.fetchMachinesAndModels
  );

  // machines and models available everywhere in the admin portal via useMachinesPortalStore
  useEffect(() => {
    fetchMachinesAndModels();
  }, [current.session]);

  return (
    <ErrorBoundary identifier="AdminPortal">
      <TeamsProvider>
        <UsersProvider>
          <Flex direction="column" height="100%">
            <AdminTabNav />
            <Routes>
              <Route path="" element={<RedirectNav />} />

              <Route path={routePaths.admin.users} element={<UsersTable />} />

              <Route
                path={routePaths.admin.machines}
                element={<MachinesTable />}
              />

              <Route
                path={routePaths.admin.organizations}
                element={sections.adminTeams ? <TeamsTable /> : <RedirectNav />}
              />

              <Route
                path={routePaths.admin.serverErrors}
                element={
                  sections.adminServerErrors ? (
                    <ServerErrorsTableHoC />
                  ) : (
                    <RedirectNav />
                  )
                }
              />

              <Route
                path={routePaths.admin.machineModels}
                element={
                  sections.adminMachineModels ? (
                    <MachineModelsTable />
                  ) : (
                    <RedirectNav />
                  )
                }
              />

              <Route
                path={routePaths.admin.ballTypes}
                element={
                  sections.adminBallTypes ? <BallTypesTable /> : <RedirectNav />
                }
              />

              <Route
                path={routePaths.admin.errorTypes}
                element={
                  sections.adminErrorTypes ? (
                    <ErrorTypesTableHoc />
                  ) : (
                    <RedirectNav />
                  )
                }
              />

              <Route
                path={routePaths.admin.staticVideos}
                element={
                  sections.adminStaticVideos ? (
                    <VideoLibrary identifier="AdminPortalVideoLibrary" static />
                  ) : (
                    <RedirectNav />
                  )
                }
              />

              <Route
                path={routePaths.admin.apiKeys}
                element={
                  sections.adminApiKeys ? (
                    <ApiKeysManagement />
                  ) : (
                    <RedirectNav />
                  )
                }
              />

              <Route
                path={routePaths.admin.serverControls}
                element={
                  sections.adminServerControls ? (
                    <ServerControlsTab />
                  ) : (
                    <RedirectNav />
                  )
                }
              />

              <Route path="*" element={<RedirectNav />} />
            </Routes>
          </Flex>
        </UsersProvider>
      </TeamsProvider>
    </ErrorBoundary>
  );
};
